import { useMemo } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { FilthBackend } from "@/api/filth-backends-api"
import type {
  FilthSearchVisualizationFeatureConfig,
  PrismView,
} from "@/features/chat/components/prism-retrieval/prism-retrieval-constants"

import { FILTH_SEARCH_VISUALIZATION_FEATURE_ID } from "@/features/chat/components/prism-retrieval/prism-retrieval-constants"
import { resolvePrismView } from "@/features/chat/components/prism-retrieval/prism-retrieval-utils"
import { queryKeys } from "@/lib/query-keys"

import { FilthControlPlaneOverview } from "./filth-control-plane-overview"
import { summarizeFilthControlPlane } from "../filth-control-plane"
import {
  useAdminFeatureConfigQuery,
  useAdminFeaturesQuery,
  useUpdateFeatureConfigMutation,
  useUpdateFeatureStatusMutation,
} from "../hooks/queries/features"

export function FilthControlPlanePanel({ backends }: { backends: FilthBackend[] }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { data: features, isLoading } = useAdminFeaturesQuery()
  const updateFeatureStatusMutation = useUpdateFeatureStatusMutation()
  const visualizationFeature = features?.find(
    (feature) => feature.id === FILTH_SEARCH_VISUALIZATION_FEATURE_ID
  )
  const { data: visualizationConfig } =
    useAdminFeatureConfigQuery<FilthSearchVisualizationFeatureConfig>(
      FILTH_SEARCH_VISUALIZATION_FEATURE_ID,
      visualizationFeature !== undefined
    )
  const updateFeatureConfigMutation =
    useUpdateFeatureConfigMutation<FilthSearchVisualizationFeatureConfig>()
  // The backend normalizes a stored config (missing or unknown -> "levels");
  // resolve once more so the select never holds a value it cannot show.
  const defaultView =
    visualizationConfig === undefined
      ? undefined
      : resolvePrismView(undefined, visualizationConfig.default_view)
  const summary = useMemo(() => summarizeFilthControlPlane(backends), [backends])

  const handleDefaultViewChange = async (view: PrismView) => {
    try {
      await updateFeatureConfigMutation.mutateAsync({
        featureId: FILTH_SEARCH_VISUALIZATION_FEATURE_ID,
        config: { default_view: view },
      })
      await queryClient.invalidateQueries({ queryKey: queryKeys.config })
      toast.success(
        t("adminDashboard.filthControlPlane.defaultViewUpdated", "Default view updated")
      )
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t("adminDashboard.filthControlPlane.updateFailed", "Failed to update search experience")
      )
    }
  }

  const handleVisualizationChange = async (enabled: boolean) => {
    try {
      await updateFeatureStatusMutation.mutateAsync({
        featureId: FILTH_SEARCH_VISUALIZATION_FEATURE_ID,
        enabled,
      })
      await queryClient.invalidateQueries({ queryKey: queryKeys.config })
      toast.success(
        enabled
          ? t("adminDashboard.filthControlPlane.enabled", "Secure neural retrieval enabled")
          : t("adminDashboard.filthControlPlane.disabled", "Secure neural retrieval disabled")
      )
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t("adminDashboard.filthControlPlane.updateFailed", "Failed to update search experience")
      )
    }
  }

  return (
    <FilthControlPlaneOverview
      defaultView={defaultView}
      defaultViewPending={updateFeatureConfigMutation.isPending}
      summary={summary}
      visualizationAvailable={!isLoading && visualizationFeature !== undefined}
      visualizationEnabled={visualizationFeature?.enabled ?? false}
      visualizationPending={updateFeatureStatusMutation.isPending}
      onDefaultViewChange={(view) => void handleDefaultViewChange(view)}
      onVisualizationChange={(enabled) => void handleVisualizationChange(enabled)}
    />
  )
}
